import { useEffect, useMemo, useState } from "react";
import { api } from "./api/api";
import { formatNumber } from "./utils/numberFormat";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// import logo from "./assets/logo.png"; // No longer needed directly here
import generateBolInvoicePdf from "./generateBolInvoicePdf";
import generateDeliveryOrderPdf from "./generateDeliveryOrderPdf";
import generateReceiptPdf from "./generateReceiptPdf";
import generateAirCargoManifestPdf from "./generatManifest";

import TabStock from "./TabStock";
import TabReprint from "./TabReprint";
import TabWarehouseFinancials from "./TabWarehouseFinancials";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDateToYMD_forReprint = (dateString) => {
  if (!dateString || typeof dateString !== "string") return "";
  return dateString.slice(0, 10);
};

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

// ─── Query String Helper ─────────────────────────────────────────────────────

function qs(obj) {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ReportsScreen({ permissions: permsProp }) {
  const permissions = permsProp || readPerms();
  const canView = useMemo(() => has(permissions, "USERS_ADMIN"), [permissions]);

  // Selected Report State
  const [selectedReport, setSelectedReport] = useState("manifest_list");

  // Shared Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Flights
  const [flights, setFlights] = useState([]);
  const [flightNo, setFlightNo] = useState("");

  // Manifest
  const [manifests, setManifests] = useState([]);

  // Stock
  const [stockQuery, setStockQuery] = useState("");
  const [stockItems, setStockItems] = useState([]);
  const [stockSummary, setStockSummary] = useState(null);
  const [stockMoves, setStockMoves] = useState([]);
  const [stockByService, setStockByService] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // Reprint state
  const [reprintQuery, setReprintQuery] = useState("");
  const [reprintResult, setReprintResult] = useState(null);
  const [reprintCurrency, setReprintCurrency] = useState("USD");
  const [reprintExchangeRate, setReprintExchangeRate] = useState(2500);

  // ✅ نفس منطق طباعة فاتورة التخزين: كتالوجات الخدمات/التغليف/البناء لحل عرض البيانات في إعادة الطباعة
  const [servicesList, setServicesList] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);

  // ✅ حالات جديدة لتقرير قائمة المانيفست
  const [manifestReportData, setManifestReportData] = useState([]);
  const [manifestSearch, setManifestSearch] = useState("");
  const [previewManifest, setPreviewManifest] = useState(null);

  // Split stock movements into IN and OUT
  const inMoves = useMemo(() => stockMoves.filter((m) => m.move_type === "IN"), [stockMoves]);
  const outMoves = useMemo(() => stockMoves.filter((m) => m.move_type === "OUT"), [stockMoves]);

  // Load manifests list
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get("/manifests/list");
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setManifests(list);
      } catch {
        setManifests([]);
      }
    })();
  }, [canView]);

  // Load flight numbers
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get(`/reports/flights${qs({ from: dateFrom, to: dateTo })}`);
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        const normalized = list
          .map((x) => (typeof x === "string" ? x : x?.flight_no || x?.flightNo || ""))
          .filter(Boolean);
        setFlights(normalized);
      } catch {
        setFlights([]);
      }
    })();
  }, [canView, dateFrom, dateTo]);

  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get('/services/exchange-rate');
        if (res?.rate != null) setReprintExchangeRate(Number(res.rate));
      } catch {
        setReprintExchangeRate(2500);
      }
    })();
  }, [canView]);

  // ✅ تحميل كتالوجات الخدمات/التغليف/البناء (نفس منطق شاشة إصدار البوالص) لحل عرض None/$0 في إعادة الطباعة
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await api.get('/services');
        if (Array.isArray(res)) {
          const mapped = res.map(s => {
            const flattenedPrices = {};
            if (s.prices) {
              Object.keys(s.prices).forEach(key => {
                flattenedPrices[key.toLowerCase()] = s.prices[key];
              });
            }
            return { ...s, ...flattenedPrices };
          });
          setServicesList(mapped);
        }
      } catch {
        setServicesList([]);
      }
      try {
        const res = await api.get('/packing');
        setPackingItems(Array.isArray(res) ? res : []);
      } catch {
        setPackingItems([]);
      }
      try {
        const res = await api.get('/build-up');
        setBuildUpItems(Array.isArray(res) ? res : []);
      } catch {
        setBuildUpItems([]);
      }
    })();
  }, [canView]);

  // Permission guard (after all hooks)
  if (!canView) {
    return (
      <div className="card">
        <h3>Reports</h3>
        <div className="message error">You don't have permission</div>
      </div>
    );
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  // ✅ دالة جلب قائمة المانيفستات بدلاً من التصدير المباشر
  const loadManifestListReport = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/manifest-list${qs({ from: dateFrom, to: dateTo, q: manifestSearch })}`);
      setManifestReportData(res || []);
      if (!res?.length) setMsg("No manifests found matching criteria.");
    } catch (e) {
      setMsg(e?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  // ✅ دالة لعرض تفاصيل المانيفست في قسم المعاينة
  const handleViewManifest = async (m) => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/manifests/${m.id}/full`);
      setPreviewManifest(res);
      setMsg(`Manifest #${m.id} loaded for preview.`);
    } catch (e) {
      setMsg("Error loading manifest details: " + (e?.message || "Unknown error"));
      setPreviewManifest(null);
    } finally {
      setLoading(false);
    }
  };

  // ✅ دالة لطباعة المانيفست أو Load Breakdown Sheet
  const handlePrintManifest = (manifestData, type = 'manifest') => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    generateAirCargoManifestPdf({
      header: { ...manifestData.header, id: manifestData.header.id },
      bols: manifestData.bols.flatMap(b => [b, ...(b.homes || [])]), // تسطيح البوالص (ماستر وهاوس)
      totals: { total_packages: manifestData.header.total_packages || 0, total_gross_mass: manifestData.header.total_gross_mass || 0 },
      user,
      title: type === 'manifest' ? "AIR CARGO MANIFEST" : "LOAD BREAKDOWN SHEET",
      fileNamePrefix: type === 'manifest' ? "AirCargoManifest" : "LoadBreakdownSheet"
    });

  };

  const loadStock = async () => {
    try {
      setLoading(true);
      setMsg("");
      const [items, summary, moves] = await Promise.all([
        api.get(`/reports/stock${qs({ q: stockQuery })}`),
        api.get(`/reports/stock-summary${qs({ from: dateFrom, to: dateTo })}`),
        api.get(`/reports/stock-movements${qs({ from: dateFrom, to: dateTo, ref: stockQuery || undefined })}`),
      ]);
      setStockItems(Array.isArray(items) ? items : []);
      setStockSummary(summary || null);
      setStockMoves(Array.isArray(moves) ? moves : []);
      setMsg("Updated Warehouse report ✅");
    } catch (e) {
      setMsg(e?.message || "Failed to fetch Warehouse report");
    } finally {
      setLoading(false);
    }
  };

  const loadStockByService = async () => {
    try {
      setLoading(true);
      setMsg("");
      const res = await api.get(`/reports/stock-by-service${qs({ q: stockQuery })}`);
      setStockByService(res || null);
      setMsg("Updated Stock By Service report ✅");
    } catch (e) {
      setStockByService(null);
      setMsg(e?.message || "Failed to fetch Stock By Service report");
    } finally {
      setLoading(false);
    }
  };

  const loadStockSummary = async () => {
    try {
      setLoading(true);
      setMsg("");
      const s = await api.get(`/reports/stock-summary${qs({ from: dateFrom, to: dateTo })}`);
      setStockSummary(s || null);
    } catch (e) {
      setMsg(e?.message || "Failed to fetch stock summary");
      setStockSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReprintSearch = async () => {
    if (!reprintQuery.trim()) return;
    setLoading(true);
    setMsg("");
    setReprintResult(null);
    try {
      const res = await api.get(`/bols/for-reprint/${encodeURIComponent(reprintQuery.trim())}`);
      setReprintResult(res);
      setMsg("AWB data loaded successfully.");
    } catch (e) {
      setReprintResult(null);
      // ✅ رسالة خطأ أكثر تفصيلاً
      let errorMessage = "Failed to find AWB.";
      if (e?.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      setMsg(errorMessage);
    } finally {
      setLoading(false);
    }
  };

const handleReprintDO = (data) => {
  if (!data?.bol) {
    setMsg("Cannot print receipt, BOL data is missing.");
    return;
  }

  const { bol } = data;

  const savedReceiptAmount =
    bol.receipt_amount ||
    bol.bd_receipt_amount ||
    bol.receiptAmount ||
    0;

  const receiptNo =
    bol.bd_receipt_id ||
    bol.receipt_no ||
    bol.receiptNo;

  generateReceiptPdf({
    bol,
    savedReceiptAmount,
    receipt_no: receiptNo,
    currency: reprintCurrency,
    exchangeRate: reprintExchangeRate,
  });

  setMsg("Receipt re-printed successfully.");
};

const handleReprintInvoice = (data) => {
  if (!data?.bol || !data?.header || !data?.service) {
    setMsg("Cannot print invoice, data is missing.");
    return;
  }

  const { bol, header, service } = data;

  // القيم المحفوظة
  // ✅ الأولوية لمبلغ التخزين الصافي (405) لضمان تطابق البيانات
  const savedInvoiceAmount = 
    Number(bol.storage_amount) > 0 ? bol.storage_amount : 
    (bol.bd_invoice_amount || bol.invoice_amount || 0);


  const savedReceiptAmount =
    bol.receipt_amount ||
    bol.bd_receipt_amount ||
    bol.receiptAmount ||
    0;

  // الأيام المخزنة
  const savedDays =
    bol.billable_days ??
    bol.storage_days ??
    bol.billableDays;

  let days = 1;

  if (savedDays != null) {
    days = Number(savedDays);
  } else {
    const arrivalDate = new Date(header.date_of_arrival);
    const deliveryDate = new Date(
      bol.delivery_date || new Date()
    );

    if (!isNaN(arrivalDate.getTime())) {
      const diffMs =
        deliveryDate.getTime() - arrivalDate.getTime();

      days =
        Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;

      if (bol.day === 1) days -= 1;

      days = Math.max(1, days);
    }
  }

  const discountAmount = Number(bol.discount || 0);

  generateBolInvoicePdf({
    bol,
    header,
    service,
    servicesList: Array.isArray(servicesList) ? servicesList : [],
    packingItems: Array.isArray(packingItems) ? packingItems : [],
    buildUpItems: Array.isArray(buildUpItems) ? buildUpItems : [],
    recipientName: bol.delivered_to,
    deliveryDate: bol.delivery_date,
    days,
    discount: discountAmount,

    d_no: bol.d_no || bol.bd_invoice_id,

    savedInvoiceAmount,
    savedReceiptAmount,
    currency: reprintCurrency,
    exchangeRate: reprintExchangeRate,
  });

  setMsg("Invoice re-printed successfully.");
};

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="screen screen-rtl" style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'flex-start' }}>
      {/* ─── Sidebar Menu ─── */}
      <div className="card" style={{ width: '250px', flexShrink: 0, padding: '15px' }}>
        <h3 style={{ borderBottom: '2px solid #2e7d32', paddingBottom: '10px' }}>Reports Menu</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '15px' }}>
          <p className="hint-text" style={{ fontWeight: 'bold', color: '#2e7d32', marginTop: '10px' }}>Manifests</p>
          <button className={`btn ${selectedReport === 'manifest_list' ? 'active-tab' : ''}`} onClick={() => setSelectedReport('manifest_list')}>General Manifest List</button>
          
          <p className="hint-text" style={{ fontWeight: 'bold', color: '#2e7d32', marginTop: '10px' }}>Warehouse & Stock</p>
          <button className={`btn ${selectedReport === 'warehouse_financials' ? 'active-tab' : ''}`} onClick={() => setSelectedReport('warehouse_financials')}>Warehouse Financials</button>
          <button className={`btn ${selectedReport === 'stock_inventory' ? 'active-tab' : ''}`} onClick={() => setSelectedReport('stock_inventory')}>Inventory Status</button>
          <button className={`btn ${selectedReport === 'stock_moves' ? 'active-tab' : ''}`} onClick={() => setSelectedReport('stock_moves')}>Stock Movements</button>
          <button className={`btn ${selectedReport === 'stock_service' ? 'active-tab' : ''}`} onClick={() => setSelectedReport('stock_service')}>Stock by Service</button>

          <p className="hint-text" style={{ fontWeight: 'bold', color: '#2e7d32', marginTop: '10px' }}>Documents</p>
          <button className={`btn ${selectedReport === 'reprint' ? 'active-tab' : ''}`} onClick={() => setSelectedReport('reprint')}>Reprint AWB Documents</button>
        </div>
      </div>

      {/* ─── Main Content Area ─── */}
      <div style={{ flex: 1 }}>
        {/* Status Message */}
        {msg && (
          <div className={`message ${msg.toLowerCase().includes("fail") || msg.toLowerCase().includes("error") ? "error" : "success"}`} style={{ marginBottom: '15px' }}>
            {msg}
          </div>
        )}

        {/* Report Content */}
        <div className="card">
          {/* 1. Manifest Reports Section */}
          {selectedReport === 'manifest_list' && (
            <div>
              <h3>Manifest List Report</h3>
              <div className="grid grid-auto small-gap" style={{ marginBottom: '20px', background: '#e8f5e9', padding: '15px', borderRadius: '8px' }}>
                <div className="field">
                  <label>Date From</label>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="field">
                  <label>Date To</label>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className="field">
                  <label>Search (ID / Flight / Reg)</label>
                  <input
                    value={manifestSearch}
                    onChange={(e) => setManifestSearch(e.target.value)}
                    placeholder="Enter manifest ID or flight..."
                  />
                </div>
              </div>
              <div className="actions-row">
                <button
                  className="btn-primary"
                  disabled={loading}
                  onClick={loadManifestListReport}>
                  {loading ? 'Processing...' : 'Load Manifest List'}
                </button>
              </div>

              {/* ✅ عرض النتائج في جدول مع خيارات العرض والطباعة */}
              {manifestReportData.length > 0 && (
                <div className="table-wrap" style={{ marginTop: '20px' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Flight No.</th>
                        <th>Arrival</th>
                        <th>Owner</th>
                        <th>AWBs</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifestReportData.map(m => (
                        <tr key={m.id}>
                          <td>{m.id}</td>
                          <td>{m.voyage_number}</td>
                          <td>{m.date_of_arrival ? String(m.date_of_arrival).slice(0, 10) : ""}</td>
                          <td>{m.owner_operator}</td>
                          <td>{m.actual_total_bols}</td>
                          <td>
                             <span className={`badge ${m.is_departed ? "badge-gcr" : "badge-dg"}`}>
                                {m.is_departed ? "Departed" : "Pending"}
                             </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '5px' }}>
                              <button className="btn-small" onClick={() => handleViewManifest(m)}>View</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ✅ قسم معاينة تفاصيل المنفست المختار */}
              {previewManifest && (
                <div className="card" style={{ marginTop: 20, border: '2px solid #2e7d32' }}>
                  <div className="card-header-row">
                    <h4>Manifest #{previewManifest.header.id} Preview</h4>
                    <button className="danger-btn btn-small" onClick={() => setPreviewManifest(null)}>Close Preview</button>
                  </div>
                  <div className="table-wrap" style={{ marginTop: 10 }}>
                    <table className="data-table" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th>AWB</th>
                          <th>Consignee</th>
                          <th>Weight</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewManifest.bols.map(b => (
                          <tr key={b.id}>
                            <td>{b.bol_reference}</td>
                            <td>{b.consignee_name}</td>
                            <td>{b.gross_mass} KG</td>
                            <td>{b.goods_description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="actions-row">
                     <button className="btn-primary" onClick={() => handlePrintManifest(previewManifest, 'manifest')}>Print Air Cargo Manifest</button>
                     <button className="btn-secondary" onClick={() => handlePrintManifest(previewManifest, 'load_breakdown')}>Print Load Breakdown Sheet</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 2. Warehouse Reports Section */}
          {(selectedReport === 'stock_inventory' || selectedReport === 'stock_moves' || selectedReport === 'stock_service') && (
            <div>
              <h3>{selectedReport === 'stock_inventory' ? 'Inventory Status' : selectedReport === 'stock_moves' ? 'Movement History' : 'Warehouse Details by Service'}</h3>
              
              {/* Specific Filter for Inventory/Moves */}
              {selectedReport !== 'stock_service' && (
                <div className="grid grid-auto small-gap" style={{ marginBottom: '20px', background: '#f1f8e9', padding: '15px', borderRadius: '8px' }}>
                   <div className="field">
                    <label>From Date</label>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>AWB Ref Search</label>
                    <input value={stockQuery} onChange={(e) => setStockQuery(e.target.value)} placeholder="Filter by AWB..." />
                  </div>
                </div>
              )}

              <div className="actions-row" style={{ marginBottom: '20px' }}>
                <button 
                  className="btn-primary" 
                  disabled={loading} 
                  onClick={selectedReport === 'stock_service' ? loadStockByService : loadStock}
                >
                  {loading ? 'Processing...' : selectedReport === 'stock_service' ? 'Load Stock by Service' : 'Update Warehouse Data'}
                </button>
              </div>

              <TabStock 
                viewMode={selectedReport}
                loading={loading}
                stockItems={stockItems}
                stockSummary={stockSummary}
                stockMoves={stockMoves}
                stockByService={stockByService}
                inMoves={inMoves}
                outMoves={outMoves}
                setMsg={setMsg}
              />
            </div>
          )}

          {/* 3. Reprint Section */}
          {selectedReport === 'reprint' && (
            <div>
              <h3>Document Reprint Utility</h3>
              <div className="grid grid-auto small-gap" style={{ marginBottom: '20px', background: '#fff3e0', padding: '15px', borderRadius: '8px' }}>
                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label>AWB Reference Number</label>
                  <input
                    value={reprintQuery}
                    onChange={(e) => setReprintQuery(e.target.value)}
                    placeholder="Enter full AWB reference to search..."
                    onKeyDown={(e) => e.key === 'Enter' && handleReprintSearch()}
                  />
                </div>
                <div className="field" style={{ alignSelf: 'end' }}>
                  <button className="btn-primary" onClick={handleReprintSearch} disabled={loading}>Search</button>
                </div>
                <div className="field" style={{ gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', color: '#1565c0', fontSize: '14px' }}>Reprint Currency:</span>
                    <label style={{ cursor: 'pointer', fontWeight: reprintCurrency === 'USD' ? 'bold' : 'normal', color: '#0d47a1', display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>
                      <input
                        type="radio"
                        name="reprintCurrency"
                        value="USD"
                        checked={reprintCurrency === 'USD'}
                        onChange={() => setReprintCurrency('USD')}
                      />
                      USD
                    </label>
                    <label style={{ cursor: 'pointer', fontWeight: reprintCurrency === 'SDG' ? 'bold' : 'normal', color: '#0d47a1', display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>
                      <input
                        type="radio"
                        name="reprintCurrency"
                        value="SDG"
                        checked={reprintCurrency === 'SDG'}
                        onChange={() => setReprintCurrency('SDG')}
                      />
                      SDG (Rate: {reprintExchangeRate})
                    </label>
                  </div>
                </div>
              </div>
              
              <TabReprint
                loading={loading}
                reprintResult={reprintResult}
                handleReprintSearch={handleReprintSearch}
                handleReprintDO={handleReprintDO}
                handleReprintInvoice={handleReprintInvoice}
              />
            </div>
          )}

          {/* 6. Warehouse Financials Section */}
          {selectedReport === 'warehouse_financials' && (
            <TabWarehouseFinancials dateFrom={dateFrom} dateTo={dateTo} />
          )}
        </div>
      </div>
    </div>
  );
}